import * as React from 'react';
import {Form, Tag} from 'antd';
import {FormComponentProps} from 'antd/lib/form';
import {ClientSummary, Contact, ErrorResponse, Rental, Site} from '../api/dtos/dtos';
import locales from '../locales';
import * as dateUtils from '../utils/date-utils';
import {ErrorAlert} from '../common/ErrorAlert';
import ClientSelector from '../client-details/ClientSelector';
import HiddenFormItem from '../common/form-items/HiddenFormItem';
import SelectFormItem from '../common/form-items/SelectFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import ClientSiteSelector from '../client-details/ClientSiteSelector';
import {getPaymentTypeOptions} from '../common/PaymentType';
import ClientContactSelector from '../client-details/ClientContactSelector';
import DatePickerFormItem from '../common/form-items/DatePickerFormItem';
import * as moment from 'moment';
import TextAreaFormItem from '../common/form-items/TextAreaFormItem';
import StaticFormItem from '../common/form-items/StaticFormItem';
import {EditorMode} from '../common/types';
import accountProvider from "../account/AccountProvider";
import * as formUtils from "../utils/form-utils";
import FormItem from "antd/lib/form/FormItem";


interface RentalFormProps extends FormComponentProps {
  mode: EditorMode;
  editable: boolean;
  rental?: Rental;
  onRentalChange: (rental: Rental) => void;
  onFormSubmit: () => void;
  onFormCancel: () => void;
}

interface RentalFormState {
  error?: ErrorResponse;
  contactOptions?: Contact[];
  siteOptions?: Site[];
}

const FIELD_CREATED = 'date';
const FIELD_CLIENT_ID = 'itemId';
const FIELD_PAYMENT_TYPE = 'paymentType';
const FIELD_TRANSPORT_COST = 'transportCost';
const FIELD_COMMENT = 'comment';
const FIELD_CAUTION = 'caution';

class RentalForm extends React.Component<RentalFormProps, RentalFormState> {

  public state: RentalFormState = {};

  public render() {
    const {error} = this.state;
    const {editable, rental, form, mode} = this.props;
    const client = rental ? rental.client : undefined;
    const disabled = !editable;
    const created = (rental && rental.created) ? dateUtils.toMomentOrThrow(rental.created) : moment();
    const dateEditorDisabled = disabled || mode === 'edit';
    const isAuthorised = accountProvider.isAdmin() || accountProvider.isSales();
    const isFuture = rental && rental.created ? dateUtils.toMomentOrThrow(rental.created).isAfter(moment()) : false;

    return (
      <>
        <Form>
          <StaticFormItem
            label={locales.id}
            initialValue={rental && (
              <>
                <b>{rental.idString}</b>
                {rental && rental.deleted &&
                    <Tag color="#F5222D" style={{marginLeft: 8}}>{locales.deleted}</Tag>}
              </>
            )}
          />

          <DatePickerFormItem
            form={form}
            fieldId={FIELD_CREATED}
            label={locales.rental_created}
            initialValue={created}
            disabled={dateEditorDisabled}
            required={true}
            showTime={true}
          >
            {isFuture && (
              <div>
                <Tag color="blue">{locales.future_date}</Tag>
              </div>
            )}
          </DatePickerFormItem>

          <HiddenFormItem
            form={form}
            fieldId={FIELD_CLIENT_ID}
            label={locales.rental_client}
            initialValue={client ? client.id : null}
            required={true}
          >
            <ClientSelector
              client={client}
              onSelect={this.onClientSelect}
              editable={this.isClientEditable()}
            />
          </HiddenFormItem>

          <SelectFormItem
            form={form}
            fieldId={FIELD_PAYMENT_TYPE}
            label={locales.paymentType}
            initialValue={rental ? rental.paymentType : null}
            required={true}
            disabled={disabled}
            options={getPaymentTypeOptions()}
          />

          <FormItem
            {...formUtils.formItemLayout}
            label={locales.client_representative}
          >
            <ClientContactSelector
              onContactChange={this.onRepresentativeChange}
              contact={rental && rental.representative}
              client={rental && rental.client}
              disabled={disabled}
            />
          </FormItem>

          <FormItem
            {...formUtils.formItemLayout}
            label={locales.client_responsible}
          >
            <ClientContactSelector
              onContactChange={this.onResponsibleChange}
              contact={rental && rental.responsible}
              client={rental && rental.client}
              disabled={disabled}
            />
          </FormItem>

          <FormItem {...formUtils.formItemLayout} label={locales.client_site}>
            <ClientSiteSelector
              onSiteChange={this.onSiteChange}
              site={rental && rental.site}
              client={rental && rental.client}
              disabled={disabled}
            />
          </FormItem>

          {rental?.createdBy &&
              <StaticFormItem
                  label={locales.created_by}
                  initialValue={rental.createdBy}
              />
          }
          {rental?.lastChangedBy &&
              <StaticFormItem
                  label={locales.last_changed_by}
                  initialValue={rental.lastChangedBy}
              />
          }
          {rental?.lastChangedAt &&
              <StaticFormItem
                  label={locales.last_changed_at}
                  initialValue={dateUtils.formatDateTimeOrNull(rental.lastChangedAt)}
              />
          }

          <TextAreaFormItem
            label={locales.comment}
            form={form}
            fieldId={FIELD_COMMENT}
            initialValue={rental && rental.comment}
            required={false}
            disabled={true}
            maxLength={255}
          />

          {error && <ErrorAlert style={{margin: '16px 0'}} error={error}/>}

        </Form>

        {isAuthorised && this.props.children}

        {isAuthorised && <SubmitCancelFormItem
            onSubmit={this.onFormSubmit}
            onCancel={this.onFormCancel}
            disabled={disabled}
        />}
      </>
    );
  }

  private isClientEditable = (): boolean => {
    // Client is only selectable (and changeable) in create mode
    return this.props.mode === 'create';
  };

  private onClientSelect = (client?: ClientSummary) => {
    const {rental, onRentalChange, form} = this.props;
    form.setFieldsValue({[FIELD_CLIENT_ID]: client ? client.id : undefined});
    onRentalChange({
      ...rental,
      client,
    });
  };

  private onRepresentativeChange = (representative: Contact | undefined) => {
    const {rental, onRentalChange} = this.props;
    onRentalChange({
      ...rental,
      representative
    });
  };

  private onResponsibleChange = (responsible: Contact | undefined) => {
    const {rental, onRentalChange} = this.props;
    onRentalChange({
      ...rental,
      responsible
    });
  };

  private onSiteChange = (site: Site) => {
    const {rental, onRentalChange} = this.props;
    onRentalChange({
      ...rental,
      site
    });
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {rental, onRentalChange, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onRentalChange({
          ...rental,
          created: values[FIELD_CREATED],
          transportCost: values[FIELD_TRANSPORT_COST],
          paymentType: values[FIELD_PAYMENT_TYPE],
          comment: values[FIELD_COMMENT],
          caution: values[FIELD_CAUTION],
        });
        onFormSubmit();
      }
    });
  };

}

export default Form.create<RentalFormProps>()(RentalForm);



// WEBPACK FOOTER //
// ./src/rental-details/RentalForm.tsx